import React, { useState, useEffect } from 'react';
import { callGemini, callGeminiStream, getGeminiKey } from '../../utils/gemini';
import { FB_FS, FB_KEY } from '../../utils/firebase';
import { createNewChat, generateChatTitle, getTimeAgo, getLastMessage, sortChatsByRecent, searchChats } from '../../services/chatLogic';
import AINoteAnalyzer from './AINoteAnalyzer';
import ProgressBar from '../shared/ProgressBar';

function AITab({ assignments, classes, chats, setChats, notes }) {
  const [aiMode, setAiMode] = useState('chat');

  // Chat mode state
  const [currentChatId, setCurrentChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showChatList, setShowChatList] = useState(true); // Show sidebar by default
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const titleGeneratedRef = React.useRef(new Set()); // Track which chats have had titles generated
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Detect mobile viewport changes
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setShowChatList(true); // Always show on desktop
      } else {
        setShowChatList(false); // Hide by default on mobile
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Homework helper state
  const [homeworkStep, setHomeworkStep] = useState('upload'); // 'upload' | 'analyzing' | 'selectProblems' | 'explanation' | 'check' | 'checking' | 'done'
  const [hasUploadedPhoto, setHasUploadedPhoto] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [homeworkExplanation, setHomeworkExplanation] = useState('');
  const [answerImage, setAnswerImage] = useState(null);
  const [answerFeedback, setAnswerFeedback] = useState('');
  const [homeworkMessages, setHomeworkMessages] = useState([]);
  const [homeworkInput, setHomeworkInput] = useState('');
  const [homeworkLoading, setHomeworkLoading] = useState(false);

  // NEW: Problem selector state
  const [detectedProblems, setDetectedProblems] = useState([]); // e.g. ['1a','1b','2','3']
  const [selectedProblems, setSelectedProblems] = useState(new Set());
  const [detectingProblems, setDetectingProblems] = useState(false);

  // NEW: Unrelated image state
  const [answerRejected, setAnswerRejected] = useState(false);
  const [answerRejectionMsg, setAnswerRejectionMsg] = useState('');

  // NEW: Truncation detection state
  const [responseTruncated, setResponseTruncated] = useState(false);

  // Phone upload state
  const [uploadId, setUploadId] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [checkingUploads, setCheckingUploads] = useState(false);
  const [qrSlot, setQrSlot] = useState('question');

  // Flashcards state
  const [flashcardTopic, setFlashcardTopic] = useState('');
  const [flashcards, setFlashcards] = useState([]);
  const [flashcardLoading, setFlashcardLoading] = useState(false);
  const [flashcardsReady, setFlashcardsReady] = useState(false); // Content loaded but waiting for progress bar
  const [flippedCards, setFlippedCards] = useState(new Set());

  // Writing state
  const [writingText, setWritingText] = useState('');
  const [writingFeedback, setWritingFeedback] = useState('');
  const [writingLoading, setWritingLoading] = useState(false);

  // Questions state
  const [questionsInput, setQuestionsInput] = useState('');
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsReady, setQuestionsReady] = useState(false); // Content loaded but waiting for progress bar
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState({});
  const [userTextAnswers, setUserTextAnswers] = useState({});
  const [checkingAnswer, setCheckingAnswer] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const [webcamStream, setWebcamStream] = useState(null);

  // Insights state
  const [insights, setInsights] = useState('');
  const [insightsLoading, setInsightsLoading] = useState(false);

  // ── Markdown + KaTeX renderer ─────────────────────────────────────────────────
  const renderMarkdown = (text) => {
    if (!text) return '';
    const codeBlocks = [];
    let p = text.replace(/```[\s\S]*?```/g, (m) => { codeBlocks.push(m); return `%%CB${codeBlocks.length - 1}%%`; });
    p = p.replace(/\$\$([\s\S]*?)\$\$/g, (_, math) => {
      try { return `<div style="text-align:center;margin:12px 0;overflow-x:auto;">${window.katex.renderToString(math.trim(), { displayMode: true, throwOnError: false })}</div>`; }
      catch { return `<code>$$${math}$$</code>`; }
    });
    p = p.replace(/\$([^\n$]+?)\$/g, (_, math) => {
      try { return window.katex.renderToString(math.trim(), { displayMode: false, throwOnError: false }); }
      catch { return `<code>$${math}$</code>`; }
    });
    p = p
      .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code style="background:var(--bg3);padding:2px 4px;border-radius:4px;font-family:monospace;font-size:.85em;">$1</code>')
      .replace(/^####\s(.*)$/gm, '<h4 style="font-size:1rem;font-weight:700;margin:12px 0 6px;color:var(--text);">$1</h4>')
      .replace(/^###\s(.*)$/gm,  '<h3 style="font-size:1.1rem;font-weight:700;margin:16px 0 8px;color:var(--text);">$1</h3>')
      .replace(/^##\s(.*)$/gm,   '<h2 style="font-size:1.2rem;font-weight:700;margin:20px 0 10px;color:var(--text);">$1</h2>')
      .replace(/^#\s(.*)$/gm,    '<h1 style="font-size:1.3rem;font-weight:700;margin:20px 0 10px;color:var(--text);">$1</h1>')
      .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid var(--border);margin:16px 0;">')
      .replace(/^[-*]\s(.*)$/gm, '<li style="margin:4px 0;margin-left:8px;">$1</li>')
      .replace(/^\d+\.\s(.*)$/gm, '<li style="margin:4px 0;margin-left:8px;">$1</li>')
      .replace(/\n\n/g, '</p><p style="margin:8px 0;">')
      .replace(/\n/g, '<br/>');
    p = p.replace(/(<li[^>]*>[\s\S]*?<\/li>(<br\/>)?)+/g, (m) => {
      const clean = m.replace(/<br\/>/g, '');
      return `<ul style="margin:8px 0;padding-left:20px;">${clean}</ul>`;
    });
    p = p.replace(/%%CB(\d+)%%/g, (_, i) => {
      const code = codeBlocks[parseInt(i)].replace(/^```\w*\n?/, '').replace(/```$/, '');
      return `<pre style="background:var(--bg3);padding:12px;border-radius:8px;overflow-x:auto;font-family:monospace;font-size:.82rem;margin:8px 0;"><code>${code}</code></pre>`;
    });
    return `<span>${p}</span>`;
  };

  // ── Gemini vision streaming call ─────────────────────────────────────────────
  const callGeminiWithImageStream = async (prompt, base64Image, onChunk) => {
    const b64 = base64Image.replace(/^data:image\/\w+;base64,/, '');
    const mimeMatch = base64Image.match(/^data:(image\/\w+);base64,/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:streamGenerateContent?alt=sse&key=${getGeminiKey()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }, { inline_data: { mime_type: mimeType, data: b64 } }] }],
          generationConfig: { maxOutputTokens: 8192 }
        })
      }
    );
    if (!res.ok) throw new Error(`Gemini vision error: ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let accumulated = '';
    let finishReason = null;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const lines = decoder.decode(value, { stream: true }).split('\n');
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') break;
        try {
          const parsed = JSON.parse(data);
          const piece = parsed.candidates?.[0]?.content?.parts?.[0]?.text || '';
          if (piece) { accumulated += piece; onChunk(accumulated); }
          // Check finish reason to detect truncation
          if (parsed.candidates?.[0]?.finishReason) {
            finishReason = parsed.candidates[0].finishReason;
          }
        } catch (_) {}
      }
    }
    
    // Detect truncation: check if response was cut off
    const isTruncated = finishReason === 'MAX_TOKENS' || 
                        finishReason === 'SAFETY' ||
                        (accumulated.length > 100 && !accumulated.trim().match(/[.!?]$/));
    
    return { text: accumulated, truncated: isTruncated };
  };

  // ── NEW: Gemini vision non-streaming (for JSON responses) ────────────────────
  const callGeminiWithImage = async (prompt, base64Image) => {
    const b64 = base64Image.replace(/^data:image\/\w+;base64,/, '');
    const mimeMatch = base64Image.match(/^data:(image\/\w+);base64,/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${getGeminiKey()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }, { inline_data: { mime_type: mimeType, data: b64 } }] }],
          generationConfig: { maxOutputTokens: 1024 }
        })
      }
    );
    if (!res.ok) throw new Error(`Gemini vision error: ${res.status}`);
    const data = await res.json();
    return data.candidates?.[0]?.content?.parts?.[0]?.text || '';
  };

  // ── Reset homework helper ────────────────────────────────────────────────────
  const resetHomework = () => {
    setHomeworkStep('upload');
    setHasUploadedPhoto(false);
    setUploadedImage(null);
    setHomeworkExplanation('');
    setAnswerImage(null);
    setAnswerFeedback('');
    setHomeworkMessages([]);
    setHomeworkInput('');
    setHomeworkLoading(false);
    setShowQR(false);
    setCheckingUploads(false);
    setDetectedProblems([]);
    setSelectedProblems(new Set());
    setDetectingProblems(false);
    setAnswerRejected(false);
    setAnswerRejectionMsg('');
    setResponseTruncated(false);
  };

  // ── NEW: Detect problems in homework image ────────────────────────────────────
  const detectProblemsInImage = async (imageData) => {
    setDetectingProblems(true);
    try {
      const response = await callGeminiWithImage(
        `Look at this homework image carefully. List ALL the problem/question identifiers you can see (e.g. "1a", "1b", "2", "3a", "Problem 4", etc.).

Return ONLY a JSON array of strings, nothing else. Examples:
- ["1a", "1b", "1c", "2", "3"]
- ["Problem 1", "Problem 2a", "Problem 2b"]
- ["Q1", "Q2", "Q3"]

If there is only one problem with no sub-parts, return ["1"].
Return ONLY the JSON array, no other text.`,
        imageData
      );
      const clean = response.trim().replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(clean);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch (err) {
      console.error('Problem detection failed:', err);
    }
    return [];
  };

  // ── Step 1: Handle question image upload ─────────────────────────────────────
  const processQuestionImage = async (imageData) => {
    setUploadedImage(imageData);
    setHasUploadedPhoto(true);
    setHomeworkStep('analyzing');
    setHomeworkExplanation('');
    setAnswerRejected(false);

    try {
      // Detect problems first
      const problems = await detectProblemsInImage(imageData);
      setDetectingProblems(false);

      if (problems.length > 1) {
        // Multiple problems — show selector
        setDetectedProblems(problems);
        setSelectedProblems(new Set(problems)); // all selected by default
        setHomeworkStep('selectProblems');
      } else {
        // Single problem or detection failed — proceed directly
        await analyzeHomework(imageData, []);
      }
    } catch (err) {
      setDetectingProblems(false);
      await analyzeHomework(imageData, []);
    }
  };

  // ── Analyze homework with optional problem filter ─────────────────────────────
  const analyzeHomework = async (imageData, problemsToFocus) => {
    setHomeworkStep('explanation');
    setHomeworkExplanation('');
    setResponseTruncated(false);
    const isGenerating = true; // Track that we're generating

    const focusClause = problemsToFocus.length > 0
      ? `\n\nThe student wants to focus on these specific problems/parts: ${problemsToFocus.join(', ')}. Focus your explanation primarily on these, but you may briefly mention others.`
      : '';

    try {
      const result = await callGeminiWithImageStream(
        `You are a helpful homework tutor. Look at this homework image carefully.

1. Identify exactly what type of homework this is (subject, assignment type).
2. Explain clearly and step-by-step HOW to do this homework / solve these problems.
3. Give helpful tips or strategies the student should use.
4. Do NOT give the direct answers — teach the student how to approach it.${focusClause}

Use clear markdown formatting with headers, numbered steps, and math notation where relevant.`,
        imageData,
        (streamedText) => {
          setHomeworkExplanation(streamedText);
          setTimeout(() => {
            const el = document.querySelector('.hw-explanation-scroll');
            if (el) el.scrollTop = el.scrollHeight;
          }, 50);
        }
      );
      
      // Check if response was truncated
      if (result.truncated) {
        setResponseTruncated(true);
      }
    } catch (err) {
      setHomeworkExplanation('Sorry, I had trouble analyzing your homework image. Please try again.');
    }
  };

  const handleProblemSelectionConfirm = () => {
    const selected = Array.from(selectedProblems);
    analyzeHomework(uploadedImage, selected);
  };

  const toggleProblemSelection = (problem) => {
    const next = new Set(selectedProblems);
    next.has(problem) ? next.delete(problem) : next.add(problem);
    setSelectedProblems(next);
  };

  const handleQuestionFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => processQuestionImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  // ── Check if answer image is relevant to homework ──────────────────────────────
  // ── OCR: Extract key text/numbers from homework image ──────────────────────────
  const ocrHomeworkImage = async (homeworkImageData) => {
    const b64 = homeworkImageData.replace(/^data:image\/\w+;base64,/, '');
    const mimeMatch = homeworkImageData.match(/^data:(image\/\w+);base64,/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';

    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${getGeminiKey()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [
            { text: 'Read this homework image and extract the key identifying information. List: (1) the subject/topic, (2) any specific numbers, percentages, or values mentioned in the problems (e.g. "59%", "10 users", "0.70", "13 times"), (3) any key nouns or names used (e.g. "tomato", "Ralph", "Kevin", "Internet users"). Return ONLY a JSON object: {"subject": "...", "numbers": ["59%","10","0.70",...], "keywords": ["tomato","Ralph",...]}. No other text.' },
            { inline_data: { mime_type: mimeType, data: b64 } }
          ]}],
          generationConfig: { maxOutputTokens: 400 }
        })
      }
    );
    if (!res.ok) return null;
    const data = await res.json();
    const raw = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) return null;
    try { return JSON.parse(match[0]); } catch { return null; }
  };

  // ── OCR: Extract visible text/numbers from answer image ─────────────────────
  const ocrAnswerImage = async (answerImageData) => {
    const b64 = answerImageData.replace(/^data:image\/\w+;base64,/, '');
    const mimeMatch = answerImageData.match(/^data:(image\/\w+);base64,/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';

    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${getGeminiKey()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [
            { text: 'Read all visible text and numbers from this image. Return ONLY a JSON object: {"text": "full extracted text here", "numbers": ["list","of","all","numbers","you","see"], "keywords": ["notable","words","or","names"]}. No other text.' },
            { inline_data: { mime_type: mimeType, data: b64 } }
          ]}],
          generationConfig: { maxOutputTokens: 600 }
        })
      }
    );
    if (!res.ok) return null;
    const data = await res.json();
    const raw = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) return null;
    try { return JSON.parse(match[0]); } catch { return null; }
  };

  // ── OCR homework text then verify answer matches ────────────────────────────
  const ocrImage = async (base64Image) => {
    const b64 = base64Image.replace(/^data:image\/\w+;base64,/, '');
    const mimeMatch = base64Image.match(/^data:(image\/\w+);base64,/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${getGeminiKey()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [
            { text: 'Extract all text from this image exactly as written. Include numbers, symbols, and math expressions. Output plain text only, no commentary.' },
            { inline_data: { mime_type: mimeType, data: b64 } }
          ]}],
          generationConfig: { maxOutputTokens: 1000 }
        })
      }
    );
    if (!res.ok) return '';
    const data = await res.json();
    return data.candidates?.[0]?.content?.parts?.[0]?.text || '';
  };

  const checkAnswerRelevance = async (answerImageData, homeworkImageData) => {
    try {
      // Step 1: OCR both images in parallel
      const [homeworkText, answerText] = await Promise.all([
        ocrImage(homeworkImageData),
        ocrImage(answerImageData)
      ]);

      console.log('Homework OCR:', homeworkText.slice(0, 200));
      console.log('Answer OCR:', answerText.slice(0, 200));

      if (!homeworkText || !answerText) return { related: true };

      // Step 2: Extract key identifying tokens from the homework text
      // Pull numbers, key nouns, and short phrases (3+ chars, not stopwords)
      const stopwords = new Set(['the','and','for','are','this','that','with','from','have','what','your','you','its','was','but','not','they','all','can','her','his','our','out','one','had','him','has','how','did','been','who','more','will','said','each','she','him','when','them','then','some','than','into','also','over','such','even','most','made','after','back','use','two','way','may','per','any','based','info','show','say','find','get','change','does','about','shown','survey','sample','users','change']);

      // Extract meaningful tokens from homework
      const hwTokens = homeworkText
        .toLowerCase()
        .replace(/[^a-z0-9.%]/g, ' ')
        .split(/\s+/)
        .filter(t => t.length >= 3 && !stopwords.has(t) && !/^0+$/.test(t));

      // Extract tokens from answer
      const answerTokens = new Set(
        answerText
          .toLowerCase()
          .replace(/[^a-z0-9.%]/g, ' ')
          .split(/\s+/)
          .filter(t => t.length >= 3)
      );

      // Count how many homework tokens appear in the answer
      const uniqueHwTokens = [...new Set(hwTokens)];
      const matchCount = uniqueHwTokens.filter(t => answerTokens.has(t)).length;
      const matchRatio = uniqueHwTokens.length > 0 ? matchCount / uniqueHwTokens.length : 0;

      console.log(`Token match: ${matchCount}/${uniqueHwTokens.length} = ${(matchRatio*100).toFixed(1)}%`);
      console.log('Sample hw tokens:', uniqueHwTokens.slice(0, 20));

      // If good token overlap, accept immediately without calling Gemini again
      if (matchRatio >= 0.15 || matchCount >= 8) {
        return { related: true, reason: `Answer matches homework content (${matchCount} shared terms)` };
      }

      // Step 3: Low token overlap — do a final Gemini subject-match check
      // (catches cases like same-subject but totally different worksheet)
      const b64Answer = answerImageData.replace(/^data:image\/\w+;base64,/, '');
      const mimeMatchA = answerImageData.match(/^data:(image\/\w+);base64,/);
      const mimeTypeA = mimeMatchA ? mimeMatchA[1] : 'image/jpeg';

      const b64Homework = homeworkImageData.replace(/^data:image\/\w+;base64,/, '');
      const mimeMatchH = homeworkImageData.match(/^data:(image\/\w+);base64,/);
      const mimeTypeH = mimeMatchH ? mimeMatchH[1] : 'image/jpeg';

      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${getGeminiKey()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{
              parts: [
                { text: 'Homework assignment:' },
                { inline_data: { mime_type: mimeTypeH, data: b64Homework } },
                { text: 'Student submitted answer:' },
                { inline_data: { mime_type: mimeTypeA, data: b64Answer } },
                { text: `The homework OCR text is: "${homeworkText.slice(0, 400)}"
The answer OCR text is: "${answerText.slice(0, 400)}"

Are these the same academic subject? Only return related: false if the answer is CLEARLY a completely different subject (e.g. homework is math, answer is a biology diagram or app screenshot). If they are the same subject or you are unsure, return related: true.

Respond ONLY with JSON: {"related": true, "reason": "..."} or {"related": false, "reason": "what the wrong image actually shows"}` }
              ]
            }],
            generationConfig: { maxOutputTokens: 200 }
          })
        }
      );

      if (!res.ok) return { related: true };
      const data = await res.json();
      const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
      console.log('Subject check response:', rawText);

      const jsonMatch = rawText.match(/\{[^{}]*"related"\s*:\s*(true|false)[^{}]*\}/);
      if (!jsonMatch) return { related: true };
      return JSON.parse(jsonMatch[0]);

    } catch (err) {
      console.error('Relevance check error:', err);
      return { related: true };
    }
  };

  // ── Step 2: Handle answer image upload → check answer (streaming) ─────────────
  const processAnswerImage = async (imageData) => {
    setAnswerImage(imageData);
    setAnswerRejected(false);
    setAnswerRejectionMsg('');
    setHomeworkStep('checking');
    setAnswerFeedback('');
    setResponseTruncated(false);

    // NEW: Relevance check
    if (uploadedImage) {
      const relevance = await checkAnswerRelevance(imageData, uploadedImage);
      if (!relevance.related) {
        setAnswerRejected(true);
        setAnswerRejectionMsg(relevance.reason || "This image doesn't appear to be related to your homework.");
        setHomeworkStep('explanation'); // go back to let them re-upload
        setAnswerImage(null);
        return;
      }
    }

    try {
      setHomeworkStep('done');
      setHomeworkMessages([
        { role: 'ai', text: "I've reviewed your work above! Feel free to ask me any follow-up questions." }
      ]);
      const result = await callGeminiWithImageStream(
        `You are a helpful homework tutor. The student has submitted their completed work.

Look at this image carefully and provide:
1. **Overall Assessment** — Is the work correct, partially correct, or needs revision?
2. **What's correct** — Point out specific things they got right.
3. **What needs fixing** — Point out specific errors or gaps, with explanations.
4. **How to improve** — Give concrete next steps to fix any mistakes.

Be encouraging, specific, and educational. Use markdown formatting and math notation where relevant.`,
        imageData,
        (streamedText) => {
          setAnswerFeedback(streamedText);
          setTimeout(() => {
            const el = document.querySelector('.hw-feedback-scroll');
            if (el) el.scrollTop = el.scrollHeight;
          }, 50);
        }
      );
      
      // Check if response was truncated
      if (result.truncated) {
        setResponseTruncated(true);
      }
    } catch (err) {
      setAnswerFeedback('Sorry, I had trouble reviewing your answer. Please try again.');
    }
  };

  const handleAnswerFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => processAnswerImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  // ── Phone QR upload ──────────────────────────────────────────────────────────
  const generateUploadId = () =>
    Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

  const handlePhoneUpload = (slot) => {
    const id = generateUploadId();
    setUploadId(id);
    setQrSlot(slot);
    setShowQR(true);
    setCheckingUploads(true);
  };

  useEffect(() => {
    if (!uploadId || !checkingUploads) return;
    
    // Wait 3 seconds before starting to poll (gives user time to scan QR)
    const initialDelay = setTimeout(() => {
      const checkInterval = setInterval(async () => {
        try {
          const response = await fetch(`${FB_FS}/uploads/${uploadId}?key=${FB_KEY}`);
          if (response.ok) {
            const data = await response.json();
            const imageData = data.fields?.image?.stringValue;
            if (imageData) {
              setCheckingUploads(false);
              setShowQR(false);
              if (qrSlot === 'question') {
                processQuestionImage(imageData);
              } else {
                processAnswerImage(imageData);
              }
              setTimeout(() => {
                fetch(`${FB_FS}/uploads/${uploadId}?key=${FB_KEY}`, { method: 'DELETE' }).catch(() => {});
              }, 5000);
            }
          } else if (response.status === 404) {
            // Document doesn't exist yet, this is expected - don't log error
          } else {
            // Unexpected error status
            console.warn('Upload check failed with status:', response.status);
          }
        } catch (err) {
          // Only log network errors, not 404s
          if (err.message && !err.message.includes('404')) {
            console.log('Checking for upload...', err.message);
          }
        }
      }, 3000); // Check every 3 seconds instead of 2
      
      const timeout = setTimeout(() => {
        setCheckingUploads(false);
        clearInterval(checkInterval);
      }, 5 * 60 * 1000);
      
      return () => { clearInterval(checkInterval); clearTimeout(timeout); };
    }, 3000); // Initial 3 second delay
    
    return () => { clearTimeout(initialDelay); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadId, checkingUploads]);

  const getQRCodeUrl = (text) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(text)}`;

  // ── Follow-up chat (uses full homework context) ─────────────────────────────
  const handleHomeworkSend = async () => {
    if (!homeworkInput.trim() || homeworkLoading) return;
    const userMessage = homeworkInput.trim();
    setHomeworkInput('');
    setHomeworkMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setHomeworkLoading(true);
    const aiMessageIndex = homeworkMessages.length + 1;
    setHomeworkMessages(prev => [...prev, { role: 'ai', text: '' }]);

    try {
      // Build full conversation history for Gemini
      const recentHistory = homeworkMessages.slice(-8);
      const historyParts = recentHistory.map(m => ({
        role: m.role === 'user' ? 'user' : 'model',
        parts: [{ text: m.text }]
      }));

      // Build system context from actual homework data
      const systemContext = `You are a focused homework tutor. You are ONLY helping with the specific homework assignment below.

HOMEWORK EXPLANATION YOU ALREADY PROVIDED TO THE STUDENT:
${homeworkExplanation ? homeworkExplanation.slice(0, 2000) : 'Not yet generated.'}

${answerFeedback ? `FEEDBACK YOU GAVE ON THE STUDENT'S SUBMITTED WORK:
${answerFeedback.slice(0, 2000)}` : ''}

RULES:
- Only answer questions about THIS specific homework assignment and subject.
- If the student asks about something unrelated, redirect them back to the homework.
- Be specific — reference the actual problems, numbers, and concepts from the homework above.
- Never pretend you don't know what subject or problems you were working on.
- Use markdown formatting.`;

      const messages_payload = [
        ...historyParts,
        { role: 'user', parts: [{ text: userMessage }] }
      ];

      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:streamGenerateContent?alt=sse&key=${getGeminiKey()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            system_instruction: { parts: [{ text: systemContext }] },
            contents: messages_payload,
            generationConfig: { maxOutputTokens: 4096 }
          })
        }
      );

      if (!res.ok) throw new Error(`Gemini error: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const lines = decoder.decode(value, { stream: true }).split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') break;
          try {
            const piece = JSON.parse(data).candidates?.[0]?.content?.parts?.[0]?.text || '';
            if (piece) {
              accumulated += piece;
              setHomeworkMessages(prev => {
                const msgs = [...prev];
                msgs[aiMessageIndex] = { role: 'ai', text: accumulated };
                return msgs;
              });
              setTimeout(() => {
                const el = document.querySelector('.hw-followup-chat');
                if (el) el.scrollTop = el.scrollHeight;
              }, 50);
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      setHomeworkMessages(prev => {
        const msgs = [...prev];
        msgs[aiMessageIndex] = { role: 'ai', text: 'Sorry, I encountered an error. Please try again.' };
        return msgs;
      });
    }
    setHomeworkLoading(false);
  };

  // ── Chat Management ──────────────────────────────────────────────────────────
  // Load current chat on mount or when switching chats
  useEffect(() => {
    if (!currentChatId && chats.length > 0) {
      // Load most recent chat
      const sorted = sortChatsByRecent(chats);
      setCurrentChatId(sorted[0].id);
      setMessages(sorted[0].messages || []);
    } else if (currentChatId) {
      const chat = chats.find(c => c.id === currentChatId);
      if (chat) {
        setMessages(chat.messages || []);
      }
    } else if (chats.length === 0) {
      // No chats exist, create first chat automatically
      const firstChat = createNewChat();
      setChats([firstChat]);
      setCurrentChatId(firstChat.id);
      setMessages([
        { role: 'ai', text: "Hi! I'm your AI study assistant. Ask me anything about studying!" }
      ]);
    }
  }, [currentChatId, chats]);

  // Save messages to current chat
  useEffect(() => {
    if (!currentChatId || messages.length === 0) return;
    
    // Update chat with new messages (use functional update to avoid dependency on chats)
    setChats(prevChats => {
      const chat = prevChats.find(c => c.id === currentChatId);
      if (!chat) return prevChats;
      
      // Check if messages actually changed to prevent unnecessary updates
      const messagesChanged = JSON.stringify(chat.messages) !== JSON.stringify(messages);
      if (!messagesChanged) return prevChats;
      
      return prevChats.map(c => 
        c.id === currentChatId 
          ? { ...c, messages, updatedAt: new Date().toISOString() }
          : c
      );
    });
    
    // Generate title if this is a new chat with 2+ messages and we haven't generated one yet
    setChats(prevChats => {
      const chat = prevChats.find(c => c.id === currentChatId);
      if (!chat) return prevChats;
      
      const needsTitle = chat.title === 'New Chat' && 
                         messages.length >= 2 && 
                         !titleGeneratedRef.current.has(currentChatId);
      
      if (needsTitle) {
        titleGeneratedRef.current.add(currentChatId);
        generateChatTitle(messages).then(title => {
          setChats(prev => prev.map(c => 
            c.id === currentChatId ? { ...c, title } : c
          ));
        });
      }
      
      return prevChats;
    });
  }, [messages, currentChatId, setChats]);

  const startNewChat = () => {
    const newChat = createNewChat();
    // Add welcome message to the new chat
    newChat.messages = [
      { role: 'ai', text: "Hi! I'm your AI study assistant. Ask me anything about studying!" }
    ];
    setChats(prev => [...prev, newChat]);
    setCurrentChatId(newChat.id);
    setMessages(newChat.messages);
    // Keep sidebar open when creating new chat
  };

  const switchChat = (chatId) => {
    setCurrentChatId(chatId);
    // Keep sidebar open when switching chats
  };

  const deleteChat = (chatId, e) => {
    e.stopPropagation();
    setChats(prevChats => {
      if (prevChats.length === 1) {
        // Don't delete last chat, just clear it
        titleGeneratedRef.current.delete(chatId); // Reset title generation flag
        setMessages([{ role: 'ai', text: "Hi! I'm your AI study assistant. Ask me anything about studying!" }]);
        return prevChats.map(c => 
          c.id === chatId 
            ? { ...c, messages: [{ role: 'ai', text: "Hi! I'm your AI study assistant. Ask me anything about studying!" }], title: 'New Chat' }
            : c
        );
      } else {
        titleGeneratedRef.current.delete(chatId); // Clean up ref
        const filtered = prevChats.filter(c => c.id !== chatId);
        if (currentChatId === chatId) {
          const sorted = sortChatsByRecent(filtered);
          setCurrentChatId(sorted[0].id);
        }
        return filtered;
      }
    });
  };

  const currentChat = chats.find(c => c.id === currentChatId);

  // ── Chat ─────────────────────────────────────────────────────────────────────
  const handleChatSend = async () => {
    if (!input.trim() || loading) return;
    
    // Create new chat if none exists
    if (!currentChatId) {
      const newChat = createNewChat();
      newChat.messages = []; // Start with empty messages
      setChats(prev => [...prev, newChat]);
      setCurrentChatId(newChat.id);
    }
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setLoading(true);
    const aiMessageIndex = messages.length + 1;
    setMessages(prev => [...prev, { role: 'ai', text: '' }]);
    try {
      const context = `You are a helpful AI study assistant. Help with general study questions, learning strategies, and academic advice.

Current student context:
- Has ${assignments.length} assignments
- Taking ${classes.length} classes: ${classes.map(c => c.name).join(', ')}

Provide helpful, encouraging responses about studying and academics. Use markdown formatting for better readability.`;
      const history = messages.slice(-6);
      await callGeminiStream(userMessage, context,
        (streamedText) => {
          setMessages(prev => {
            const msgs = [...prev];
            msgs[aiMessageIndex] = { role: 'ai', text: streamedText };
            return msgs;
          });
          setTimeout(() => {
            const el = document.querySelector('.chat-container');
            if (el) el.scrollTop = el.scrollHeight;
          }, 50);
        },
        history
      );
    } catch (err) {
      setMessages(prev => {
        const msgs = [...prev];
        msgs[aiMessageIndex] = { role: 'ai', text: 'Sorry, I encountered an error. Please try again.' };
        return msgs;
      });
    }
    setLoading(false);
  };

  // ── Flashcards ───────────────────────────────────────────────────────────────
  const generateFlashcards = async () => {
    if (!flashcardTopic.trim() || flashcardLoading) return;
    setFlashcardLoading(true);
    setFlashcardsReady(false); // Content not ready yet
    setFlashcards([]);
    setFlippedCards(new Set());
    try {
      const context = `Create exactly 6 study flashcards for: ${flashcardTopic}
You are an expert educator. Make questions that test understanding, not just memorization.
Format as JSON array:
[
  {"question": "What is the quadratic formula?", "answer": "x = (-b ± √(b²-4ac)) / 2a"},
  ...
]
Return ONLY the JSON array with exactly 6 flashcards. No other text.`;
      const response = await callGemini(context);
      let cards = [];
      try {
        const clean = response.trim().replace(/```json\n?/g, '').replace(/```\n?/g, '');
        const parsed = JSON.parse(clean);
        if (Array.isArray(parsed) && parsed.length > 0) cards = parsed.slice(0, 6);
      } catch (_) {}
      if (cards.length === 0) {
        const lines = response.split('\n').map(line => line.trim()).filter(line => line.length > 0);
        let currentCard = {};
        for (const line of lines) {
          if (line.match(/^(\d+\.|\*|\-|Q:|Question:)/i) || line.includes('?')) {
            if (currentCard.question && currentCard.answer) { cards.push(currentCard); currentCard = {}; }
            currentCard.question = line.replace(/^(\d+\.|\*|\-|Q:|Question:)\s*/i, '').trim();
          } else if (line.match(/^(A:|Answer:)/i) || (currentCard.question && !currentCard.answer && line.length > 10)) {
            currentCard.answer = line.replace(/^(A:|Answer:)\s*/i, '').trim();
          }
        }
        if (currentCard.question && currentCard.answer) cards.push(currentCard);
      }
      if (cards.length === 0) {
        cards = [
          {question: `What is ${flashcardTopic}?`, answer: `${flashcardTopic} is an important topic that requires study and understanding.`},
          {question: `Why is ${flashcardTopic} important?`, answer: `Understanding ${flashcardTopic} helps build foundational knowledge in this subject area.`},
          {question: `What are the main concepts in ${flashcardTopic}?`, answer: `The main concepts include key definitions, principles, and applications related to ${flashcardTopic}.`}
        ];
      }
      setFlashcards(cards.slice(0, 6));
      setFlashcardsReady(true); // Mark content as ready
    } catch (err) {
      setFlashcards([{ question: `Study topic: ${flashcardTopic}`, answer: "Sorry, couldn't generate flashcards right now. Please try again." }]);
      setFlashcardsReady(true); // Mark as ready even on error so progress bar completes
    }
    setFlashcardTopic('');
  };

  const toggleFlashcard = (index) => {
    const next = new Set(flippedCards);
    next.has(index) ? next.delete(index) : next.add(index);
    setFlippedCards(next);
  };

  // ── Writing ──────────────────────────────────────────────────────────────────
  const analyzeWriting = async () => {
    if (!writingText.trim() || writingLoading) return;
    setWritingLoading(true);
    setWritingFeedback('');
    try {
      const context = `Please provide detailed feedback on this writing. Focus on:
1. Grammar and mechanics
2. Clarity and organization
3. Content and arguments
4. Style and tone
5. Specific suggestions for improvement
Be constructive and encouraging. Use markdown formatting.`;
      await callGeminiStream(`Writing to review:\n${writingText}`, context,
        (streamedText) => setWritingFeedback(streamedText)
      );
    } catch (err) {
      setWritingFeedback("Sorry, I couldn't analyze your writing right now. Please try again.");
    }
    setWritingLoading(false);
  };

  // ── Questions ────────────────────────────────────────────────────────────────
  const openWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setWebcamStream(stream);
      setShowWebcam(true);
    } catch (err) {
      console.error('Error accessing webcam:', err);
      alert('Could not access webcam. Please check permissions or use file upload instead.');
    }
  };

  const closeWebcam = () => {
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      setWebcamStream(null);
    }
    setShowWebcam(false);
  };

  const capturePhoto = () => {
    const video = document.getElementById('webcam-video');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const imageData = canvas.toDataURL('image/jpeg');
    
    // For now, just add to input - you can extend this to actually process the image
    setQuestionsInput(prev => prev + '\n[Image captured from webcam]');
    closeWebcam();
    
    // TODO: You can add the image to a state variable to send to Gemini
    console.log('Captured image:', imageData);
  };

  const generateQuestions = async () => {
    if (!questionsInput.trim() || questionsLoading) return;
    setQuestionsLoading(true);
    setQuestionsReady(false);
    setGeneratedQuestions([]);
    setUserAnswers({});
    setShowResults({});
    setUserTextAnswers({});
    
    try {
      const prompt = `Based on the content provided, generate practice questions to test understanding.

IMPORTANT: Create actual QUESTIONS that students can answer, not definitions or explanations.

Generate exactly these types of questions:

MULTIPLE CHOICE (5 questions):
Format each as:
1. [Question text asking student to choose answer]
   A) [Option 1 text]
   B) [Option 2 text] 
   C) [Option 3 text]
   D) [Option 4 text]
   Correct Answer: [letter A, B, C, or D]
   Explanation: [brief explanation]

SHORT ANSWER (3 questions):
Format each as:
6. [Question that requires 1-2 sentence answer]
   Answer: [expected answer]
   
ESSAY/DISCUSSION (2 questions):
Format each as:
9. [Question requiring detailed explanation or comparison]
   Answer: [key points expected]

Content to analyze:
${questionsInput}`;

      const response = await callGemini(prompt, "You are a helpful AI study assistant. Generate practice questions to test understanding.");
      const questions = parseQuestions(response);
      setGeneratedQuestions(questions);
      setQuestionsReady(true); // Mark content as ready, but keep loading true
    } catch (err) {
      alert("Sorry, I couldn't generate questions right now. Please try again.");
      setQuestionsReady(true); // Mark as ready even on error so progress bar completes
    }
  };

  const parseQuestions = (response) => {
    const questions = [];
    const lines = response.split('\n');
    let currentQuestion = null;
    let questionType = 'multiple-choice';
    let collectingAnswer = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (line.match(/MULTIPLE\s+CHOICE/i)) {
        questionType = 'multiple-choice';
        continue;
      } else if (line.match(/SHORT\s+ANSWER/i)) {
        questionType = 'short-answer';
        continue;
      } else if (line.match(/ESSAY|DISCUSSION/i)) {
        questionType = 'essay';
        continue;
      }
      
      const questionMatch = line.match(/^(\d+)\.\s+(.+)/);
      if (questionMatch && questionMatch[2].length > 10) {
        if (currentQuestion && currentQuestion.text) {
          questions.push(currentQuestion);
        }
        currentQuestion = { 
          type: questionType, 
          text: questionMatch[2], 
          options: [], 
          correct: null,
          explanation: '',
          answer: ''
        };
        collectingAnswer = false;
        continue;
      }
      
      if (currentQuestion && currentQuestion.type === 'multiple-choice') {
        const optionMatch = line.match(/^([A-D])\)\s+(.+)/i);
        if (optionMatch && optionMatch[2].length > 1) {
          currentQuestion.options.push({
            letter: optionMatch[1].toUpperCase(),
            text: optionMatch[2]
          });
          continue;
        }
        
        const correctMatch = line.match(/Correct\s+Answer:\s*([A-D])/i);
        if (correctMatch) {
          currentQuestion.correct = correctMatch[1].toUpperCase();
          continue;
        }
        
        const explanationMatch = line.match(/Explanation:\s*(.+)/i);
        if (explanationMatch) {
          currentQuestion.explanation = explanationMatch[1];
          continue;
        }
      }
      
      if (currentQuestion && ['short-answer', 'essay'].includes(currentQuestion.type)) {
        const answerMatch = line.match(/Answer:\s*(.+)/i);
        if (answerMatch) {
          currentQuestion.answer = answerMatch[1];
          collectingAnswer = true;
          continue;
        }
        
        if (collectingAnswer && line && !line.match(/^\d+\./) && line.length > 0) {
          currentQuestion.answer += ' ' + line;
        }
      }
    }
    
    if (currentQuestion && currentQuestion.text && 
        ((currentQuestion.type === 'multiple-choice' && currentQuestion.options.length > 0) ||
         (['short-answer', 'essay'].includes(currentQuestion.type) && currentQuestion.answer))) {
      questions.push(currentQuestion);
    }
    
    return questions.filter(q => {
      if (!q.text || q.text.length < 10) return false;
      if (q.type === 'multiple-choice' && q.options.length < 2) return false;
      if (['short-answer', 'essay'].includes(q.type) && !q.answer) return false;
      return true;
    });
  };

  const handleMultipleChoiceAnswer = (questionIndex, selectedOption) => {
    setUserAnswers(prev => ({ ...prev, [questionIndex]: selectedOption }));
    setShowResults(prev => ({ ...prev, [questionIndex]: true }));
  };

  const handleTextAnswer = (questionIndex, answer) => {
    setUserTextAnswers(prev => ({ ...prev, [questionIndex]: answer }));
  };

  const checkTextAnswerWithAI = async (questionIndex, question) => {
    const userAnswer = userTextAnswers[questionIndex] || '';
    if (!userAnswer.trim()) {
      alert('Please provide an answer first');
      return;
    }

    setCheckingAnswer(true);
    try {
      const prompt = `You are evaluating a student's answer to a question.

Question: ${question.text}
Expected Answer: ${question.answer}
Student's Answer: ${userAnswer}

Evaluate if the student's answer is correct. Respond in this exact format:
RESULT: [CORRECT or INCORRECT]
FEEDBACK: [Brief 1-2 sentence explanation. If incorrect, explain why and what was missing. If correct, acknowledge what they got right.]`;

      const response = await callGemini(prompt, "You are a helpful study assistant evaluating student answers. Be encouraging but accurate.");
      
      const resultMatch = response.match(/RESULT:\s*(CORRECT|INCORRECT)/i);
      const feedbackMatch = response.match(/FEEDBACK:\s*(.+)/is);
      
      const isCorrect = resultMatch && resultMatch[1].toUpperCase() === 'CORRECT';
      const feedback = feedbackMatch ? feedbackMatch[1].trim() : response;
      
      setShowResults(prev => ({ 
        ...prev, 
        [questionIndex]: { 
          shown: true, 
          isCorrect, 
          feedback 
        } 
      }));
    } catch (error) {
      console.error('Error checking answer:', error);
      alert('Failed to check answer. Please try again.');
    } finally {
      setCheckingAnswer(false);
    }
  };

  // ── Insights ─────────────────────────────────────────────────────────────────
  const generateInsights = async () => {
    if (insightsLoading) return;
    setInsightsLoading(true);
    setInsights('');
    try {
      const summary = assignments.map(a =>
        `${a.title} (${a.subject}) - Due: ${a.dueDate || 'No date'} - Progress: ${a.progress}%`
      ).join('\n');
      const graded = assignments.filter(a => a.grade != null);
      const avgGrade = graded.length > 0
        ? Math.round(graded.reduce((s, a) => s + a.grade, 0) / graded.length) : null;
      const overdue = assignments.filter(a => a.progress < 100 && a.dueDate && new Date(a.dueDate) < new Date());
      const completed = assignments.filter(a => a.progress >= 100);
      const context = `Analyze this student's academic performance:
ASSIGNMENTS (${assignments.length} total):
${summary}
METRICS:
- Completed: ${completed.length}/${assignments.length}
- Overdue: ${overdue.length}
- Avg grade: ${avgGrade || 'N/A'}%
- Classes: ${classes.map(c => c.name).join(', ')}
Provide insights on study patterns, subject performance, areas to improve, and next steps. Use markdown.`;
      await callGeminiStream('Please analyze my academic performance.', context,
        (streamedText) => setInsights(streamedText)
      );
    } catch (err) {
      setInsights("Sorry, I couldn't generate insights right now. Please try again.");
    }
    setInsightsLoading(false);
  };

  // ── Shared UI components ─────────────────────────────────────────────────────
  const ChatMessage = ({ msg, isUser }) => (
    <div style={{ display:'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 16, width:'100%' }}>
      <div style={{ display:'flex', gap:12, alignItems:'flex-start', maxWidth:'80%', flexDirection: isUser ? 'row-reverse' : 'row' }}>
        <div style={{ width:32, height:32, borderRadius:'50%', background: isUser ? 'var(--accent)' : '#6366f1',
          display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'.8rem', fontWeight:700, flexShrink:0 }}>
          {isUser ? '👤' : '🤖'}
        </div>
        <div style={{ background: isUser ? 'var(--accent)' : 'var(--card)', color: isUser ? '#fff' : 'var(--text)',
          padding:'12px 16px', borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          border: isUser ? 'none' : '1.5px solid var(--border)', fontSize:'.85rem', lineHeight:1.6,
          fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
          <div dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }} />
        </div>
      </div>
    </div>
  );

  const ThinkingBubble = () => (
    <div style={{ display:'flex', justifyContent:'flex-start', marginBottom:16 }}>
      <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
        <div style={{ width:32, height:32, borderRadius:'50%', background:'#6366f1', display:'flex',
          alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'.8rem', fontWeight:700 }}>🤖</div>
        <div style={{ background:'var(--card)', padding:'12px 16px', borderRadius:'16px 16px 16px 4px',
          border:'1.5px solid var(--border)', fontSize:'.85rem', color:'var(--text3)' }}>Thinking...</div>
      </div>
    </div>
  );

  const UploadButtons = ({ slot, onFile, onCamera, onPhone }) => (
    <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'center' }}>
      <input type="file" accept="image/*" onChange={onFile} style={{ display:'none' }} id={`file-${slot}`} />
      <label htmlFor={`file-${slot}`} className="btn btn-p">📁 Upload File</label>
      <input type="file" accept="image/*" capture="environment" onChange={onCamera} style={{ display:'none' }} id={`cam-${slot}`} />
      <button className="btn btn-p" onClick={() => document.getElementById(`cam-${slot}`).click()} style={{display:"flex",alignItems:"center",gap:4}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>
        </svg>
        Take Photo
      </button>
      <button className="btn btn-p" onClick={onPhone} style={{display:"flex",alignItems:"center",gap:4}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
        </svg>
        Use Phone
      </button>
    </div>
  );

  const QRModal = () => (
    showQR && uploadId ? (
      <div style={{ marginTop:20, padding:16, background:'var(--bg3)', borderRadius:12, textAlign:'center' }}>
        <div style={{ fontWeight:600, marginBottom:8, color:'var(--text)' }}>Scan with your phone:</div>
        <img src={getQRCodeUrl(`${window.location.origin}/upload/${uploadId}`)} alt="QR Code"
          style={{ borderRadius:8, background:'#fff', padding:8 }} />
        <div style={{ fontSize:'.8rem', color:'var(--text2)', margin:'8px 0' }}>
          Or visit: {window.location.origin}/upload/{uploadId}
        </div>
        {checkingUploads && (
          <div style={{ fontSize:'.8rem', color:'var(--accent)', fontWeight:600, marginBottom:8 }}>Waiting for upload...</div>
        )}
        <button className="btn btn-g btn-sm" onClick={() => { setShowQR(false); setCheckingUploads(false); }}>Close</button>
      </div>
    ) : null
  );

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>
      <div className="sec-hd">
        <div className="sec-t">AI Study Assistant</div>
        <span style={{ fontSize:'.75rem', color:'var(--text3)', fontWeight:600 }}>Powered by Gemini AI</span>
      </div>

      {/* Mode tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
        {[['chat','Chat'],['homework','Homework Helper'],['flashcards','Flashcards'],['writing','Writing'],['questions','Questions'],['notes','Note Analyzer'],['insights','Insights']]
          .map(([mode, label]) => (
            <button key={mode} className={`btn btn-sm ${aiMode === mode ? 'btn-p' : 'btn-g'}`}
              onClick={() => setAiMode(mode)}>{label}</button>
          ))}
      </div>

      {/* ── CHAT ── */}
      {aiMode === 'chat' && (
        <div style={{ flex:1, display:'flex', gap:12, overflow:'hidden', height:'100%', position:'relative' }}>
          {/* Mobile overlay backdrop */}
          {isMobile && showChatList && (
            <div 
              onClick={() => setShowChatList(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 998,
                backdropFilter: 'blur(4px)'
              }}
            />
          )}

          {/* Chat List Sidebar - Responsive */}
          <div style={{ 
            width: isMobile ? (showChatList ? '280px' : '0px') : (showChatList ? '240px' : '0px'),
            transition: 'transform 0.3s ease, width 0.3s ease',
            transform: isMobile && showChatList ? 'translateX(0)' : isMobile ? 'translateX(-100%)' : 'translateX(0)',
            position: isMobile ? 'fixed' : 'relative',
            left: isMobile ? 0 : 'auto',
            top: isMobile ? 0 : 'auto',
            bottom: isMobile ? 0 : 'auto',
            zIndex: isMobile ? 999 : 'auto',
            overflow: 'hidden',
            display:'flex',
            flexDirection:'column',
            background:'var(--card)',
            border: isMobile ? 'none' : '1.5px solid var(--border)',
            borderRight: isMobile ? '1.5px solid var(--border)' : '1.5px solid var(--border)',
            borderRadius: isMobile ? 0 : 12,
            flexShrink: 0,
            boxShadow: isMobile ? '4px 0 12px rgba(0,0,0,0.15)' : 'none'
          }}>
            <div style={{ padding:'12px 12px 10px', borderBottom:'1.5px solid var(--border)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <div style={{ fontWeight:700, fontSize:'.9rem', color:'var(--text)' }}>Chats</div>
                <button 
                  className="btn btn-sm btn-p"
                  onClick={startNewChat}
                  style={{ padding:'4px 10px', fontSize:'.8rem' }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                </button>
              </div>
              <input
                type="text"
                placeholder="Search..."
                value={chatSearchQuery}
                onChange={e => setChatSearchQuery(e.target.value)}
                style={{
                  width:'100%',
                  padding:'6px 10px',
                  border:'1.5px solid var(--border)',
                  borderRadius:6,
                  background:'var(--bg2)',
                  color:'var(--text)',
                  fontSize:'.8rem',
                  outline:'none'
                }}
              />
            </div>
            <div style={{ flex:1, overflow:'auto', padding:'6px' }}>
              {sortChatsByRecent(searchChats(chats, chatSearchQuery)).map(chat => (
                <div
                  key={chat.id}
                  onClick={() => switchChat(chat.id)}
                  style={{
                    padding:'10px',
                    borderRadius:8,
                    marginBottom:4,
                    cursor:'pointer',
                    background: currentChatId === chat.id ? 'var(--accent)' : 'transparent',
                    color: currentChatId === chat.id ? '#fff' : 'var(--text)',
                    transition:'all 0.2s',
                    position:'relative'
                  }}
                  onMouseEnter={e => {
                    if (currentChatId !== chat.id) {
                      e.currentTarget.style.background = 'var(--bg3)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (currentChatId !== chat.id) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{ 
                    display:'flex', 
                    justifyContent:'space-between', 
                    alignItems:'flex-start',
                    marginBottom:3
                  }}>
                    <div style={{ 
                      fontWeight:600, 
                      fontSize:'.8rem',
                      flex:1,
                      overflow:'hidden',
                      textOverflow:'ellipsis',
                      whiteSpace:'nowrap',
                      paddingRight:6
                    }}>
                      {chat.title}
                    </div>
                    <button
                      onClick={(e) => deleteChat(chat.id, e)}
                      style={{
                        background:'none',
                        border:'none',
                        color: currentChatId === chat.id ? '#fff' : 'var(--text3)',
                        cursor:'pointer',
                        padding:0,
                        opacity:0.6,
                        transition:'opacity 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                  <div style={{ 
                    fontSize:'.7rem',
                    opacity:0.8,
                    overflow:'hidden',
                    textOverflow:'ellipsis',
                    whiteSpace:'nowrap',
                    marginBottom:3
                  }}>
                    {getLastMessage(chat.messages || [])}
                  </div>
                  <div style={{ fontSize:'.65rem', opacity:0.7 }}>
                    {getTimeAgo(chat.updatedAt)}
                  </div>
                </div>
              ))}
              {chats.length === 0 && (
                <div style={{ 
                  textAlign:'center', 
                  padding:'30px 15px', 
                  color:'var(--text3)',
                  fontSize:'.8rem'
                }}>
                  No chats yet. Start a new conversation!
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, overflow:'hidden' }}>
            {/* Chat Header */}
            <div style={{ 
              padding:'10px 14px',
              borderBottom:'1.5px solid var(--border)',
              display:'flex',
              alignItems:'center',
              gap:10,
              background:'var(--card)',
              borderRadius:'12px 12px 0 0',
              border:'1.5px solid var(--border)',
              borderBottom:'none',
              flexShrink: 0
            }}>
              <button
                onClick={() => setShowChatList(!showChatList)}
                className="btn btn-sm btn-g"
                style={{ padding:'6px' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12h18M3 6h18M3 18h18"/>
                </svg>
              </button>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:600, fontSize:'.85rem', color:'var(--text)' }}>
                  {currentChat?.title || 'New Chat'}
                </div>
                {currentChat && (
                  <div style={{ fontSize:'.7rem', color:'var(--text3)' }}>
                    {currentChat.messages.length} messages
                  </div>
                )}
              </div>
              <button
                onClick={startNewChat}
                className="btn btn-sm btn-g"
                title="New Chat"
                style={{ padding:'6px 10px', fontSize:'.8rem' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
              </button>
            </div>

            {/* Messages */}
            <div className="chat-container" style={{ flex:1, background:'var(--card)', border:'1.5px solid var(--border)',
              borderTop:'none', borderRadius:'0 0 12px 12px', padding:16, marginBottom:12, overflow:'auto', display:'flex', flexDirection:'column' }}>
              {messages && messages.length > 0 ? (
                messages.map((msg, i) => <ChatMessage key={i} msg={msg} isUser={msg.role === 'user'} />)
              ) : (
                <div style={{ textAlign:'center', padding:'40px 20px', color:'var(--text3)' }}>
                  Start a conversation by typing a message below!
                </div>
              )}
              {loading && <ThinkingBubble />}
            </div>

            {/* Input */}
            <div style={{ display:'flex', gap:8, flexShrink: 0 }}>
              <input type="text" value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleChatSend()}
                placeholder="Ask me anything about studying, learning strategies, or academics..."
                style={{ flex:1, padding:'10px 14px', border:'1.5px solid var(--border)', borderRadius:10,
                  background:'var(--card)', color:'var(--text)', fontSize:'.85rem', outline:'none',
                  fontFamily:"'Plus Jakarta Sans', sans-serif" }} />
              <button onClick={handleChatSend} disabled={!input.trim() || loading}
                style={{ padding:'10px 18px', border:'none', borderRadius:10,
                  background: !input.trim() || loading ? 'var(--border)' : 'var(--accent)',
                  color:'#fff', fontWeight:600, cursor: !input.trim() || loading ? 'not-allowed' : 'pointer',
                  fontFamily:"'Plus Jakarta Sans', sans-serif" }}>Send</button>
            </div>
          </div>
        </div>
      )}

      {/* ── HOMEWORK HELPER ── */}
      {aiMode === 'homework' && (
        <div style={{ flex:1, display:'flex', flexDirection:'column', gap:16, overflow:'auto' }}>

          {/* Step 1: Upload */}
          {homeworkStep === 'upload' && (
            <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:24, textAlign:'center' }}>
              <div style={{ fontSize:'2.5rem', marginBottom:12 }}>
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{margin:'0 auto',display:'block'}}>
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </div>
              <div style={{ fontWeight:700, fontSize:'1.1rem', marginBottom:8, color:'var(--text)' }}>Upload Your Homework</div>
              <div style={{ fontSize:'.85rem', color:'var(--text3)', marginBottom:20, lineHeight:1.6 }}>
                Take a photo of your homework or worksheet and I'll explain exactly how to do it — step by step.
              </div>
              <UploadButtons slot="question" onFile={handleQuestionFileUpload} onCamera={handleQuestionFileUpload} onPhone={() => handlePhoneUpload('question')} />
              <QRModal />
            </div>
          )}

          {/* Step 1b: Analyzing */}
          {homeworkStep === 'analyzing' && (
            <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:24, textAlign:'center' }}>
              {uploadedImage && (
                <img src={uploadedImage} alt="Your homework"
                  style={{ width:'100%', maxWidth:400, borderRadius:8, border:'1px solid var(--border)', marginBottom:16 }} />
              )}
              <div style={{ fontSize:'1.5rem', marginBottom:8 }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{margin:'0 auto',display:'block'}}>
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
              </div>
              <div style={{ fontWeight:600, color:'var(--text)', marginBottom:4 }}>Scanning your homework...</div>
              <div style={{ fontSize:'.8rem', color:'var(--text3)', marginBottom:20 }}>Detecting problems and questions in your image.</div>
              
              <ProgressBar 
                isLoading={detectingProblems} 
                label="Analyzing image"
                showPercentage={true}
              />
            </div>
          )}

          {/* ── NEW: Step 1c: Problem Selector ── */}
          {homeworkStep === 'selectProblems' && (
            <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:24 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'1rem', color:'var(--text)', marginBottom:4 }}>Which problems do you need help with?</div>
                  <div style={{ fontSize:'.8rem', color:'var(--text3)' }}>Select the ones you want explained. All are selected by default.</div>
                </div>
                <button className="btn btn-g btn-sm" onClick={resetHomework}>↩ Start Over</button>
              </div>

              {uploadedImage && (
                <img src={uploadedImage} alt="Your homework"
                  style={{ width:'100%', maxWidth:400, borderRadius:8, border:'1px solid var(--border)', marginBottom:16, display:'block', margin:'0 auto 16px' }} />
              )}

              <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
                {detectedProblems.map((problem) => {
                  const isSelected = selectedProblems.has(problem);
                  return (
                    <button
                      key={problem}
                      onClick={() => toggleProblemSelection(problem)}
                      style={{
                        padding:'8px 16px', borderRadius:8, fontWeight:600, fontSize:'.85rem', cursor:'pointer',
                        border: isSelected ? '2px solid var(--accent)' : '2px solid var(--border)',
                        background: isSelected ? 'var(--accent)' : 'var(--bg2)',
                        color: isSelected ? '#fff' : 'var(--text)',
                        transition:'all 0.15s', fontFamily:"'Plus Jakarta Sans', sans-serif"
                      }}>
                      {problem}
                    </button>
                  );
                })}
              </div>

              <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                <button
                  className="btn btn-p"
                  onClick={handleProblemSelectionConfirm}
                  disabled={selectedProblems.size === 0}>
                  Explain Selected ({selectedProblems.size})
                </button>
                <button
                  className="btn btn-g"
                  onClick={() => {
                    setSelectedProblems(new Set(detectedProblems));
                  }}>
                  Select All
                </button>
                <button
                  className="btn btn-g"
                  onClick={() => analyzeHomework(uploadedImage, [])}>
                  Explain Everything
                </button>
              </div>
            </div>
          )}

          {/* Steps 2+: Show explanation etc */}
          {(homeworkStep === 'explanation' || homeworkStep === 'check' || homeworkStep === 'checking' || homeworkStep === 'done') && (
            <>
              {/* Homework image */}
              <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <div style={{ fontWeight:600, color:'var(--text)', fontSize:'.9rem' }}>Your Homework</div>
                  <button className="btn btn-g btn-sm" onClick={resetHomework}>↩ Start Over</button>
                </div>
                {uploadedImage && (
                  <img src={uploadedImage} alt="Your homework"
                    style={{ width:'100%', maxWidth:500, borderRadius:8, border:'1px solid var(--border)' }} />
                )}
              </div>

              {/* AI explanation card */}
              <div style={{ background:'var(--card)', border:'2px solid #6366f1', borderRadius:16, padding:20 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background:'#6366f1', display:'flex',
                    alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'1rem', flexShrink:0 }}>🤖</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, color:'var(--text)', fontSize:'.95rem' }}>How To Do This Homework</div>
                    <div style={{ fontSize:'.75rem', color:'var(--text3)' }}>
                      {homeworkExplanation ? 'Follow these steps to complete your assignment' : 'Analyzing your homework...'}
                    </div>
                  </div>
                  {!homeworkExplanation && (
                    <div style={{ width:16, height:16, border:'2px solid #6366f1', borderTopColor:'transparent',
                      borderRadius:'50%', animation:'spin 0.8s linear infinite', flexShrink:0 }} />
                  )}
                </div>
                
                {/* ── Truncation warning banner ── */}
                {responseTruncated && (
                  <div style={{
                    background:'#fef3c7', border:'1.5px solid #fbbf24', borderRadius:10,
                    padding:'12px 16px', marginBottom:16, display:'flex', gap:10, alignItems:'flex-start'
                  }}>
                    <span style={{ fontSize:'1.1rem', flexShrink:0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                    </span>
                    <div>
                      <div style={{ fontWeight:700, color:'#d97706', fontSize:'.85rem', marginBottom:4 }}>
                        Response was too long and got cut off
                      </div>
                      <div style={{ fontSize:'.82rem', color:'#b45309', lineHeight:1.5, marginBottom:8 }}>
                        The explanation was too detailed for all the problems shown. Try selecting fewer problems or uploading a clearer image with fewer questions.
                      </div>
                      <button 
                        className="btn btn-sm"
                        onClick={resetHomework}
                        style={{ 
                          background:'#d97706', 
                          color:'#fff', 
                          border:'none', 
                          padding:'6px 12px', 
                          borderRadius:6, 
                          fontSize:'.8rem',
                          fontWeight:600,
                          cursor:'pointer'
                        }}>
                        ↩ Start Over
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="hw-explanation-scroll"
                  style={{ fontSize:'.85rem', lineHeight:1.7, color:'var(--text)', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
                  {homeworkExplanation.length < 50 && (
                    <>
                      <ProgressBar 
                        isLoading={homeworkExplanation.length < 50} 
                        label="Generating explanation"
                        showPercentage={true}
                      />
                      <div style={{ color:'var(--text3)', fontStyle:'italic', textAlign:'center', marginTop:12 }}>
                        Reading your homework image and preparing step-by-step solutions...
                      </div>
                    </>
                  )}
                  {homeworkExplanation && (
                    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(homeworkExplanation) }} />
                  )}
                </div>
              </div>

              {/* Check Your Answer section */}
              {homeworkStep === 'explanation' && (
                <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:20 }}>
                  <div style={{ fontWeight:700, fontSize:'.95rem', color:'var(--text)', marginBottom:6 }}>Check Your Answer</div>
                  <div style={{ fontSize:'.85rem', color:'var(--text3)', marginBottom: answerRejected ? 12 : 16, lineHeight:1.5 }}>
                    Done with your homework? Upload a photo of your completed work and I'll tell you if it's correct.
                  </div>

                  {/* ── NEW: Rejection banner ── */}
                  {answerRejected && (
                    <div style={{
                      background:'#fef2f2', border:'1.5px solid #fca5a5', borderRadius:10,
                      padding:'12px 16px', marginBottom:16, display:'flex', gap:10, alignItems:'flex-start'
                    }}>
                      <span style={{ fontSize:'1.1rem', flexShrink:0 }}>⚠️</span>
                      <div>
                        <div style={{ fontWeight:700, color:'#dc2626', fontSize:'.85rem', marginBottom:4 }}>
                          That image doesn't match your homework
                        </div>
                        <div style={{ fontSize:'.82rem', color:'#b91c1c', lineHeight:1.5 }}>
                          {answerRejectionMsg} Please upload a photo of your <strong>actual completed work</strong> for this homework assignment.
                        </div>
                      </div>
                    </div>
                  )}

                  <UploadButtons
                    slot="answer"
                    onFile={handleAnswerFileUpload}
                    onCamera={handleAnswerFileUpload}
                    onPhone={() => handlePhoneUpload('answer')}
                  />
                  <QRModal />
                </div>
              )}

              {/* Checking answer spinner */}
              {homeworkStep === 'checking' && (
                <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:20, display:'flex', alignItems:'center', gap:14 }}>
                  <div style={{ width:40, height:40, border:'3px solid #6366f1', borderTopColor:'transparent',
                    borderRadius:'50%', animation:'spin 0.8s linear infinite', flexShrink:0 }} />
                  <div>
                    <div style={{ fontWeight:600, color:'var(--text)', fontSize:'.9rem', marginBottom:2 }}>Reviewing your work...</div>
                    <div style={{ fontSize:'.8rem', color:'var(--text3)' }}>Checking your answers — this takes a few seconds.</div>
                  </div>
                </div>
              )}

              {/* Answer feedback */}
              {homeworkStep === 'done' && (
                <>
                  <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:16 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                      <div style={{ fontWeight:600, color:'var(--text)', fontSize:'.9rem' }}>Your Answer</div>
                      <div style={{ display:'flex', gap:8 }}>
                        <input type="file" accept="image/*" onChange={handleAnswerFileUpload} style={{ display:'none' }} id="re-answer-file" />
                        <label htmlFor="re-answer-file" className="btn btn-g btn-sm">📁 Re-upload</label>
                        <input type="file" accept="image/*" capture="environment" onChange={handleAnswerFileUpload} style={{ display:'none' }} id="re-answer-cam" />
                        <button className="btn btn-g btn-sm" onClick={() => document.getElementById('re-answer-cam').click()}>📷 Retake</button>
                      </div>
                    </div>
                    {answerImage && (
                      <img src={answerImage} alt="Your answer"
                        style={{ width:'100%', maxWidth:500, borderRadius:8, border:'1px solid var(--border)' }} />
                    )}
                  </div>

                  <div style={{ background:'var(--card)', border:'2px solid #16a34a', borderRadius:16, padding:20 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
                      <div style={{ width:36, height:36, borderRadius:'50%', background:'#16a34a', display:'flex',
                        alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'1rem', flexShrink:0 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, color:'var(--text)', fontSize:'.95rem' }}>Answer Feedback</div>
                        <div style={{ fontSize:'.75rem', color:'var(--text3)' }}>
                          {answerFeedback ? "Here's how you did" : 'Reviewing your work...'}
                        </div>
                      </div>
                      {!answerFeedback && (
                        <div style={{ width:16, height:16, border:'2px solid #16a34a', borderTopColor:'transparent',
                          borderRadius:'50%', animation:'spin 0.8s linear infinite', flexShrink:0 }} />
                      )}
                    </div>
                    
                    {/* ── Truncation warning banner ── */}
                    {responseTruncated && (
                      <div style={{
                        background:'#fef3c7', border:'1.5px solid #fbbf24', borderRadius:10,
                        padding:'12px 16px', marginBottom:16, display:'flex', gap:10, alignItems:'flex-start'
                      }}>
                        <span style={{ fontSize:'1.1rem', flexShrink:0 }}>⚠️</span>
                        <div>
                          <div style={{ fontWeight:700, color:'#d97706', fontSize:'.85rem', marginBottom:4 }}>
                            Feedback was too long and got cut off
                          </div>
                          <div style={{ fontSize:'.82rem', color:'#b45309', lineHeight:1.5, marginBottom:8 }}>
                            Your work had too many problems to review completely. Try uploading a clearer image with fewer questions, or ask specific follow-up questions below.
                          </div>
                          <button 
                            className="btn btn-sm"
                            onClick={resetHomework}
                            style={{ 
                              background:'#d97706', 
                              color:'#fff', 
                              border:'none', 
                              padding:'6px 12px', 
                              borderRadius:6, 
                              fontSize:'.8rem',
                              fontWeight:600,
                              cursor:'pointer'
                            }}>
                            ↩ Start Over
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div className="hw-feedback-scroll"
                      style={{ fontSize:'.85rem', lineHeight:1.7, color:'var(--text)', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
                      <div dangerouslySetInnerHTML={{ __html: renderMarkdown(answerFeedback) }} />
                      {!answerFeedback && (
                        <div style={{ color:'var(--text3)', fontStyle:'italic' }}>Checking your answers...</div>
                      )}
                    </div>
                  </div>

                  {/* Follow-up chat */}
                  <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:'.95rem', color:'var(--text)', marginBottom:12 }}>Follow-up Questions</div>
                    <div className="hw-followup-chat" style={{ maxHeight:300, overflow:'auto', marginBottom:12 }}>
                      {homeworkMessages.map((msg, i) => <ChatMessage key={i} msg={msg} isUser={msg.role === 'user'} />)}
                      {homeworkLoading && <ThinkingBubble />}
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <input type="text" value={homeworkInput} onChange={e => setHomeworkInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleHomeworkSend()}
                        placeholder="Ask a follow-up question..."
                        style={{ flex:1, padding:'10px 14px', border:'1.5px solid var(--border)', borderRadius:10,
                          background:'var(--bg2)', color:'var(--text)', fontSize:'.85rem', outline:'none',
                          fontFamily:"'Plus Jakarta Sans', sans-serif" }} />
                      <button onClick={handleHomeworkSend} disabled={!homeworkInput.trim() || homeworkLoading}
                        style={{ padding:'10px 18px', border:'none', borderRadius:10,
                          background: !homeworkInput.trim() || homeworkLoading ? 'var(--border)' : 'var(--accent)',
                          color:'#fff', fontWeight:600, cursor: !homeworkInput.trim() || homeworkLoading ? 'not-allowed' : 'pointer',
                          fontFamily:"'Plus Jakarta Sans', sans-serif" }}>Send</button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ── FLASHCARDS ── */}
      {aiMode === 'flashcards' && (
        <div style={{ flex:1, display:'flex', flexDirection:'column' }}>
          <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:20, marginBottom:16 }}>
            <div style={{ fontWeight:600, marginBottom:12, color:'var(--text)' }}>Create Flashcards</div>
            <div style={{ display:'flex', gap:8, marginBottom:12 }}>
              <input type="text" value={flashcardTopic} onChange={e => setFlashcardTopic(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && generateFlashcards()}
                placeholder="Enter topic (e.g., 'Biology Chapter 5', 'Spanish Vocabulary')..."
                style={{ flex:1, padding:'10px 12px', border:'1.5px solid var(--border)', borderRadius:8,
                  background:'var(--bg2)', color:'var(--text)', fontSize:'.85rem', outline:'none',
                  fontFamily:"'Plus Jakarta Sans', sans-serif" }} />
              <button className="btn btn-p" onClick={generateFlashcards} disabled={!flashcardTopic.trim() || flashcardLoading}>
                {flashcardLoading ? 'Creating...' : 'Generate'}
              </button>
            </div>
            <div style={{ fontSize:'.8rem', color:'var(--text3)' }}>Tip: Be specific for better flashcards</div>
          </div>
          
          {flashcardLoading && (
            <div style={{ padding:20, background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:12 }}>
              <ProgressBar 
                isLoading={flashcardLoading && !flashcardsReady} 
                label="Generating flashcards"
                showPercentage={true}
                onComplete={() => {
                  setTimeout(() => {
                    setFlashcardLoading(false);
                    setFlashcardsReady(false);
                  }, 100);
                }}
              />
            </div>
          )}
          
          {flashcards.length > 0 && !flashcardLoading && (
            <div style={{ flex:1 }}>
              <div style={{ fontSize:'.9rem', fontWeight:600, color:'var(--text)', marginBottom:16 }}>
                Your Flashcards ({flashcards.length}) — Click to flip!
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:16, marginBottom:16, perspective:1000 }}>
                {flashcards.map((card, index) => {
                  const isFlipped = flippedCards.has(index);
                  return (
                    <div key={index} onClick={() => toggleFlashcard(index)}
                      style={{ 
                        background:'var(--card)', 
                        border:'1.5px solid var(--border)', 
                        borderRadius:12,
                        minHeight:200, 
                        maxHeight:250, 
                        cursor:'pointer', 
                        position:'relative',
                        transformStyle:'preserve-3d', 
                        transition:'transform 0.6s',
                        transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                      }}>
                      {/* Front of card (Question) */}
                      <div style={{ 
                        position:'absolute', 
                        width:'100%', 
                        height:'100%', 
                        backfaceVisibility:'hidden',
                        WebkitBackfaceVisibility:'hidden',
                        display:'flex', 
                        flexDirection:'column', 
                        padding:20,
                        borderRadius:12, 
                        boxSizing:'border-box',
                        background:'var(--card)'
                      }}>
                        <div style={{ fontSize:'.8rem', color:'var(--accent)', fontWeight:600, marginBottom:12, textAlign:'center', flexShrink:0 }}>QUESTION</div>
                        <div style={{ 
                          fontSize:'.85rem', 
                          color:'var(--text)', 
                          lineHeight:1.4, 
                          textAlign:'center',
                          flex:1, 
                          wordWrap:'break-word', 
                          overflowWrap:'break-word', 
                          hyphens:'auto', 
                          padding:'8px 4px',
                          overflowY:'auto',
                          minHeight:0
                        }}>
                          {card.question}
                        </div>
                        <div style={{ fontSize:'.75rem', color:'var(--text4)', textAlign:'center', marginTop:12, flexShrink:0 }}>Click to reveal answer</div>
                      </div>
                      
                      {/* Back of card (Answer) */}
                      <div style={{ 
                        position:'absolute', 
                        width:'100%', 
                        height:'100%', 
                        backfaceVisibility:'hidden',
                        WebkitBackfaceVisibility:'hidden',
                        transform:'rotateY(180deg)', 
                        display:'flex', 
                        flexDirection:'column', 
                        padding:20, 
                        borderRadius:12, 
                        background:'var(--card)', 
                        boxSizing:'border-box'
                      }}>
                        <div style={{ fontSize:'.8rem', color:'#16a34a', fontWeight:600, marginBottom:12, textAlign:'center', flexShrink:0 }}>ANSWER</div>
                        <div style={{ 
                          fontSize:'.85rem', 
                          color:'var(--text)', 
                          lineHeight:1.4, 
                          textAlign:'center',
                          flex:1, 
                          wordWrap:'break-word', 
                          overflowWrap:'break-word', 
                          hyphens:'auto', 
                          padding:'8px 4px', 
                          overflowY:'auto',
                          minHeight:0
                        }}>
                          {card.answer}
                        </div>
                        <div style={{ fontSize:'.75rem', color:'var(--text4)', textAlign:'center', marginTop:12, flexShrink:0 }}>Click to see question</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ textAlign:'center' }}>
                <button className="btn btn-g" onClick={() => { setFlashcards([]); setFlippedCards(new Set()); }} style={{ marginRight:8 }}>Clear Cards</button>
                <button className="btn btn-g" onClick={() => setFlippedCards(new Set())}>Reset All</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── WRITING ── */}
      {aiMode === 'writing' && (
        <div style={{ flex:1, display:'flex', flexDirection:'column' }}>
          <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:20, marginBottom:16 }}>
            <div style={{ fontWeight:600, marginBottom:12, color:'var(--text)' }}>Writing Feedback</div>
            <textarea value={writingText} onChange={e => setWritingText(e.target.value)}
              placeholder="Paste your writing here for detailed feedback on grammar, structure, and content..."
              style={{ width:'100%', minHeight:150, padding:12, border:'1.5px solid var(--border)', borderRadius:8,
                background:'var(--bg2)', color:'var(--text)', fontSize:'.85rem', outline:'none', resize:'vertical',
                fontFamily:"'Plus Jakarta Sans', sans-serif", marginBottom:12 }} />
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:'.8rem', color:'var(--text3)' }}>{writingText.length} characters</div>
              <button className="btn btn-p" onClick={analyzeWriting} disabled={!writingText.trim() || writingLoading}>
                {writingLoading ? 'Analyzing...' : 'Get Feedback'}
              </button>
            </div>
          </div>
          {writingFeedback && (
            <div style={{ flex:1, background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:20, overflow:'auto' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                <div style={{ width:32, height:32, borderRadius:'50%', background:'#6366f1', display:'flex',
                  alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'.8rem', fontWeight:700 }}>🤖</div>
                <div style={{ fontWeight:600, color:'var(--text)' }}>Writing Feedback</div>
              </div>
              <div style={{ fontSize:'.85rem', lineHeight:1.6, color:'var(--text)', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
                <div dangerouslySetInnerHTML={{ __html: renderMarkdown(writingFeedback) }} />
              </div>
              <div style={{ marginTop:16, textAlign:'center' }}>
                <button className="btn btn-g btn-sm" onClick={() => setWritingFeedback('')}>Clear Feedback</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── QUESTIONS ── */}
      {aiMode === 'questions' && (
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'auto' }}>
          <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:20, marginBottom:16 }}>
            <div style={{ fontWeight:600, marginBottom:12, color:'var(--text)' }}>Generate Practice Questions</div>
            <div style={{ fontSize:'.85rem', color:'var(--text3)', marginBottom:16 }}>
              Enter text, upload a file, or take a picture to generate practice questions.
            </div>
            <textarea value={questionsInput} onChange={e => setQuestionsInput(e.target.value)}
              placeholder="Paste your notes or study material here..."
              style={{ width:'100%', minHeight:120, padding:12, border:'1.5px solid var(--border)', borderRadius:8,
                background:'var(--bg2)', color:'var(--text)', fontSize:'.85rem', outline:'none', resize:'vertical',
                fontFamily:"'Plus Jakarta Sans', sans-serif", marginBottom:12 }} />
            <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
              <label className="btn btn-g btn-sm" style={{ cursor:'pointer' }}>
                📁 Upload File
                <input type="file" accept=".txt,.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif" style={{ display:'none' }} />
              </label>
              {/Mobi|Android/i.test(navigator.userAgent) ? (
                <label className="btn btn-g btn-sm" style={{ cursor:'pointer' }}>
                  📷 Take Picture
                  <input type="file" accept="image/*" capture="environment" style={{ display:'none' }} />
                </label>
              ) : (
                <button className="btn btn-g btn-sm" onClick={openWebcam}>
                  📷 Take Picture
                </button>
              )}
            </div>
            <button className="btn btn-p" onClick={generateQuestions} disabled={!questionsInput.trim() || questionsLoading}>
              {questionsLoading ? 'Generating Questions...' : 'Generate Questions'}
            </button>
          </div>
          
          {questionsLoading && (
            <div style={{ padding:20, background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:12, marginTop:16 }}>
              <ProgressBar 
                isLoading={questionsLoading && !questionsReady} 
                label="Generating practice questions"
                showPercentage={true}
                onComplete={() => {
                  setTimeout(() => {
                    setQuestionsLoading(false);
                    setQuestionsReady(false);
                  }, 100);
                }}
              />
            </div>
          )}

          {/* Webcam Modal */}
          {showWebcam && (
            <div style={{
              position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:9999,
              display:'flex', alignItems:'center', justifyContent:'center'
            }}>
              <div style={{
                background:'var(--card)', borderRadius:16, padding:20, maxWidth:600, width:'90%',
                display:'flex', flexDirection:'column', gap:16
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ fontWeight:600, fontSize:'1.1rem', color:'var(--text)' }}>Take a Picture</div>
                  <button onClick={closeWebcam} style={{
                    background:'none', border:'none', fontSize:'1.5rem', cursor:'pointer', color:'var(--text3)'
                  }}>×</button>
                </div>
                <video
                  id="webcam-video"
                  autoPlay
                  playsInline
                  ref={video => {
                    if (video && webcamStream) {
                      video.srcObject = webcamStream;
                    }
                  }}
                  style={{ width:'100%', borderRadius:8, background:'#000' }}
                />
                <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                  <button className="btn btn-p" onClick={capturePhoto}>
                    📸 Capture
                  </button>
                  <button className="btn btn-g" onClick={closeWebcam}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {generatedQuestions.length > 0 && !questionsLoading && (
            <div style={{ flex:1, background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:20, overflow:'auto' }}>
              <h3 style={{ marginBottom:16, color:'var(--accent)', fontSize:'1.1rem', fontWeight:700 }}>Practice Questions</h3>
              {generatedQuestions.map((q, idx) => (
                <div key={idx} style={{ marginBottom:24, padding:16, background:'var(--bg3)', borderRadius:10 }}>
                  <div style={{ fontWeight:600, marginBottom:12, color:'var(--text)' }} dangerouslySetInnerHTML={{ __html: renderMarkdown(`${idx + 1}. ${q.text}`) }} />
                  
                  {/* Multiple Choice Questions */}
                  {q.options && q.options.length > 0 && (
                    <div style={{ marginLeft:16 }}>
                      {q.options.map((opt, optIdx) => {
                        const optionLetter = opt.letter;
                        const isSelected = userAnswers[idx] === optionLetter;
                        const isCorrect = optionLetter === q.correct;
                        const showResult = showResults[idx];
                        
                        return (
                          <div
                            key={optIdx}
                            onClick={() => !showResult && handleMultipleChoiceAnswer(idx, optionLetter)}
                            style={{
                              padding:12,
                              marginBottom:8,
                              border: `2px solid ${
                                showResult && isCorrect ? '#10b981' :
                                showResult && isSelected && !isCorrect ? '#ef4444' :
                                isSelected ? 'var(--accent)' : 'var(--border)'
                              }`,
                              borderRadius:8,
                              background: showResult && isCorrect ? '#10b98110' :
                                         showResult && isSelected && !isCorrect ? '#ef444410' :
                                         isSelected ? 'var(--accent)10' : 'var(--card)',
                              cursor: showResult ? 'default' : 'pointer',
                              transition:'all 0.2s ease',
                              display:'flex',
                              alignItems:'center',
                              gap:12
                            }}
                          >
                            <div style={{
                              width:24,
                              height:24,
                              borderRadius:'50%',
                              border: `2px solid ${
                                showResult && isCorrect ? '#10b981' :
                                showResult && isSelected && !isCorrect ? '#ef4444' :
                                isSelected ? 'var(--accent)' : 'var(--border)'
                              }`,
                              background: isSelected ? (
                                showResult && isCorrect ? '#10b981' :
                                showResult && !isCorrect ? '#ef4444' : 'var(--accent)'
                              ) : 'transparent',
                              display:'flex',
                              alignItems:'center',
                              justifyContent:'center',
                              color:'white',
                              fontSize:'.75rem',
                              fontWeight:600,
                              flexShrink:0
                            }}>
                              {isSelected && (showResult ? (isCorrect ? '✓' : '✗') : optionLetter)}
                            </div>
                            <div style={{ flex:1 }}>
                              <span style={{ fontWeight: showResult && isCorrect ? 600 : 'normal', color:'var(--text)' }}>
                                <strong>{optionLetter})</strong> <span dangerouslySetInnerHTML={{ __html: renderMarkdown(opt.text) }} />
                              </span>
                            </div>
                            {showResult && isCorrect && (
                              <span style={{ color:'#10b981', fontWeight:600, fontSize:'.85rem' }}>✓ Correct</span>
                            )}
                            {showResult && !isCorrect && isSelected && (
                              <span style={{ color:'#ef4444', fontWeight:600, fontSize:'.85rem' }}>✗ Wrong</span>
                            )}
                          </div>
                        );
                      })}
                      {showResults[idx] && q.explanation && (
                        <div style={{ marginTop:12, padding:12, background:'var(--bg2)', borderRadius:8,
                          borderLeft:'4px solid var(--accent)', fontSize:'.85rem', color:'var(--text2)' }} dangerouslySetInnerHTML={{ __html: renderMarkdown(`**💡 Explanation:** ${q.explanation}`) }} />
                      )}
                    </div>
                  )}
                  
                  {/* Text Answer Questions */}
                  {(!q.options || q.options.length === 0) && q.answer && (
                    <div style={{ marginLeft:16 }}>
                      <textarea
                        placeholder={q.type === 'essay' ? "Write your detailed answer here..." : "Type your answer here..."}
                        value={userTextAnswers[idx] || ''}
                        onChange={(e) => handleTextAnswer(idx, e.target.value)}
                        disabled={showResults[idx]?.shown}
                        style={{
                          width:'100%',
                          minHeight: q.type === 'essay' ? 120 : 80,
                          padding:12,
                          border: `2px solid ${
                            showResults[idx]?.shown ? 
                              (showResults[idx]?.isCorrect ? '#10b981' : '#ef4444') : 'var(--border)'
                          }`,
                          borderRadius:8,
                          background:'var(--card)',
                          color:'var(--text)',
                          fontSize:'.85rem',
                          resize:'vertical',
                          fontFamily:'inherit'
                        }}
                      />
                      <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:12 }}>
                        {!showResults[idx]?.shown && (
                          <button
                            onClick={() => checkTextAnswerWithAI(idx, q)}
                            disabled={!userTextAnswers[idx]?.trim() || checkingAnswer}
                            style={{
                              padding:'10px 20px',
                              background: userTextAnswers[idx]?.trim() && !checkingAnswer ? 'var(--accent)' : 'var(--bg3)',
                              color: userTextAnswers[idx]?.trim() && !checkingAnswer ? 'white' : 'var(--text3)',
                              border:'none',
                              borderRadius:8,
                              fontSize:'.85rem',
                              fontWeight:600,
                              cursor: userTextAnswers[idx]?.trim() && !checkingAnswer ? 'pointer' : 'not-allowed',
                              transition:'all 0.2s ease',
                              alignSelf:'flex-start'
                            }}
                          >
                            {checkingAnswer ? 'Checking...' : '✓ Check Answer'}
                          </button>
                        )}
                        {showResults[idx]?.shown && (
                          <div style={{ 
                            padding:16, 
                            background: showResults[idx]?.isCorrect ? '#10b98115' : '#ef444415',
                            border: `2px solid ${showResults[idx]?.isCorrect ? '#10b981' : '#ef4444'}`,
                            borderRadius:8,
                            fontSize:'.85rem'
                          }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                              <span style={{ fontSize:20, color: showResults[idx]?.isCorrect ? '#10b981' : '#ef4444' }}>
                                {showResults[idx]?.isCorrect ? '✓' : '✗'}
                              </span>
                              <span style={{ 
                                color: showResults[idx]?.isCorrect ? '#10b981' : '#ef4444', 
                                fontWeight:700,
                                fontSize:'.95rem'
                              }}>
                                {showResults[idx]?.isCorrect ? 'Correct!' : 'Incorrect'}
                              </span>
                            </div>
                            <div style={{ color:'var(--text2)', lineHeight:1.5 }}>
                              <strong>Feedback:</strong> {showResults[idx]?.feedback}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── NOTE ANALYZER ── */}
      {aiMode === 'notes' && (
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          <AINoteAnalyzer 
            notes={notes || []} 
            assignments={assignments}
            darkMode={document.body.classList.contains('dark')}
          />
        </div>
      )}

      {/* ── INSIGHTS ── */}
      {aiMode === 'insights' && (
        <div style={{ flex:1, display:'flex', flexDirection:'column' }}>
          <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:20, marginBottom:16 }}>
            <div style={{ fontWeight:600, marginBottom:12, color:'var(--text)' }}>Study Insights</div>
            <div style={{ fontSize:'.85rem', color:'var(--text2)', marginBottom:16, lineHeight:1.5 }}>
              Get personalized insights based on your {assignments.length} assignments and {classes.length} classes.
            </div>
            <button className="btn btn-p" onClick={generateInsights} disabled={insightsLoading || assignments.length === 0}>
              {insightsLoading ? 'Analyzing...' : 'Generate Insights'}
            </button>
            {assignments.length === 0 && (
              <div style={{ fontSize:'.8rem', color:'var(--text4)', marginTop:8 }}>Add some assignments first to get personalized insights!</div>
            )}
          </div>
          {insights && (
            <div style={{ flex:1, background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:16, padding:20, overflow:'auto' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                <div style={{ width:32, height:32, borderRadius:'50%', background:'#6366f1', display:'flex',
                  alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'.8rem', fontWeight:700 }}>🤖</div>
                <div style={{ fontWeight:600, color:'var(--text)' }}>Your Study Insights</div>
              </div>
              <div style={{ fontSize:'.85rem', lineHeight:1.6, color:'var(--text)', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>
                <div dangerouslySetInnerHTML={{ __html: renderMarkdown(insights) }} />
              </div>
              <div style={{ marginTop:16, textAlign:'center' }}>
                <button className="btn btn-g btn-sm" onClick={() => setInsights('')}>Clear Insights</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AITab;
